import type { ProgrammeEntry } from "@/lib/core/types";
import { tokenizeString } from "./tokenizeString";

export interface ScoreTriple {
  current: string;
  min: string;
  max: string;
}

export interface ElectiveScore {
  subject: string;
  grade: string;
  score: number;
  baseScore?: number;
  weighting?: number;
}

export interface CalculatedScores {
  eng: number;
  chin: number;
  math: number;
  csd: string;
  bestElectives: ElectiveScore[];
  allElectivesRanked: ElectiveScore[];
}

export interface ScoreCalculationResult {
  minScore: number;
  currentScore: number;
  maxScore: number;
  calculatedScores: CalculatedScores;
}

// Grade to numeric value conversion
export const gradeValues: Record<string, number> = {
  "5**": 7,
  "5*": 6,
  "5": 5,
  "4": 4,
  "3": 3,
  "2": 2,
  "1": 1,
  "U": 0,
  "Attained": 0,
};

// Category C Language Grade Conversions by University
export const getCategoryCLanguageScore = (
  grade: string,
  instKey: string,
  subjectAbbr: string
): number => {
  // French, German, Spanish (CEFR grades)
  if (["FRENCH", "GERMAN", "SPANISH"].includes(subjectAbbr)) {
    const cefrGrades: Record<string, Record<string, number>> = {
      hku: { "C2": 8.5, "C1": 8.5, "B2": 7, "B1": 5.5, "A2": 4, "N/A": 0 },
      cuhk: { "C2": 7, "C1": 5.5, "B2": 4, "B1": 3, "A2": 3, "N/A": 0 },
      hkust: { "C2": 8.5, "C1": 7, "B2": 5.5, "B1": 4, "A2": 3, "N/A": 0 },
      cityu: { "C2": 7, "C1": 5.5, "B2": 4, "B1": 3, "A2": 3, "N/A": 0 },
      polyu: { "C2": 8.5, "C1": 7, "B2": 5.5, "B1": 4, "A2": 3, "N/A": 0 },
      hkbu: { "C2": 7, "C1": 7, "B2": 5.5, "B1": 5.5, "A2": 4, "N/A": 0 },
      lingnan: { "C2": 7, "C1": 7, "B2": 6, "B1": 5, "A2": 4, "N/A": 0 },
      eduhk: { "C2": 7, "C1": 6, "B2": 5, "B1": 4, "A2": 3, "N/A": 0 },
    };
    return cefrGrades[instKey]?.[grade] || 0;
  }

  // Korean (TOPIK grades) only using Grade naming, remove Level and other variants
  if (subjectAbbr === "KOREAN") {
    const koreanGrades: Record<string, Record<string, number>> = {
      hku: {
        "Grade 6": 8.5,
        "Grade 5": 7,
        "Grade 4": 5.5,
        "Grade 3": 4,
        "N/A": 0,
      },
      cuhk: {
        "Grade 6": 7,
        "Grade 5": 5.5,
        "Grade 4": 4,
        "Grade 3": 3,
        "N/A": 0,
      },
      hkust: {
        "Grade 6": 8.5,
        "Grade 5": 5.5,
        "Grade 4": 4,
        "Grade 3": 3,
        "N/A": 0,
      },
      cityu: {
        "Grade 6": 7,
        "Grade 5": 5.5,
        "Grade 4": 4,
        "Grade 3": 3,
        "N/A": 0,
      },
      polyu: {
        "Grade 6": 8.5,
        "Grade 5": 7,
        "Grade 4": 4,
        "Grade 3": 3,
        "N/A": 0,
      },
      hkbu: {
        "Grade 6": 7,
        "Grade 5": 5.5,
        "Grade 4": 5.5,
        "Grade 3": 4,
        "N/A": 0,
      },
      lingnan: {
        "Grade 6": 7,
        "Grade 5": 6,
        "Grade 4": 5,
        "Grade 3": 4,
        "N/A": 0,
      },
      eduhk: {
        "Grade 6": 7,
        "Grade 5": 6,
        "Grade 4": 5,
        "Grade 3": 4,
        "N/A": 0,
      },
    };
    return koreanGrades[instKey]?.[grade] || 0;
  }

  // Japanese (JLPT grades)
  if (subjectAbbr === "JAPANESE") {
    const japaneseGrades: Record<string, Record<string, number>> = {
      hku: { N1: 8.5, N2: 7, N3: 4, "N/A": 0 },
      cuhk: { N1: 7, N2: 5.5, N3: 4, "N/A": 0 },
      hkust: { N1: 8.5, N2: 5.5, N3: 3, "N/A": 0 },
      cityu: { N1: 7, N2: 5.5, N3: 4, "N/A": 0 },
      polyu: { N1: 8.5, N2: 5.5, N3: 3, "N/A": 0 },
      hkbu: { N1: 7, N2: 5.5, N3: 4, "N/A": 0 },
      lingnan: { N1: 7, N2: 6, N3: 4, "N/A": 0 },
      eduhk: { N1: 7, N2: 5, N3: 4, "N/A": 0 },
    };
    return japaneseGrades[instKey]?.[grade] || 0;
  }

  // Urdu (GCE grades)
  if (subjectAbbr === "URDU") {
    const urduGrades: Record<string, Record<string, number>> = {
      hku: {
        "A++": 8.5,
        "A+": 8.5,
        A: 8.5,
        "B++": 7,
        "B+": 5.5,
        B: 4,
        C: 3,
        D: 2,
        E: 1,
        "N/A": 0,
      },
      cuhk: {
        "A++": 7,
        "A+": 7,
        A: 7,
        "B++": 5.5,
        "B+": 5.5,
        B: 4,
        C: 4,
        D: 3,
        E: 3,
        "N/A": 0,
      },
      hkust: {
        "A++": 8.5,
        "A+": 7,
        A: 7,
        "B++": 5.5,
        "B+": 4,
        B: 3,
        C: 3,
        D: 2.5,
        E: 1,
        "N/A": 0,
      },
      cityu: {
        "A++": 7,
        "A+": 7,
        A: 7,
        "B++": 5.5,
        "B+": 5.5,
        B: 4,
        C: 4,
        D: 3,
        E: 3,
        "N/A": 0,
      },
      polyu: {
        "A++": 8.5,
        "A+": 8.5,
        A: 7,
        "B++": 5.5,
        "B+": 5.5,
        B: 4,
        C: 3,
        D: 2,
        E: 2,
        "N/A": 0,
      },
      hkbu: {
        "A++": 7,
        "A+": 7,
        A: 7,
        "B++": 5.5,
        "B+": 4,
        B: 4,
        C: 2.5,
        D: 2.5,
        E: 1,
        "N/A": 0,
      },
      lingnan: {
        "A++": 7,
        "A+": 7,
        A: 7,
        "B++": 6,
        "B+": 5,
        B: 4,
        C: 0,
        D: 0,
        E: 0,
        "N/A": 0,
      },
      eduhk: {
        "A++": 0,
        "A+": 0,
        A: 0,
        "B++": 0,
        "B+": 0,
        B: 0,
        C: 0,
        D: 0,
        E: 0,
        "N/A": 0,
      },
    };
    return urduGrades[instKey]?.[grade] || 0;
  }

  return 0;
};

/**
 * Get subject weighting from programme
 * @param programme - Programme entry with calculation details
 * @param subjectCode - Subject code (e.g., 'englishLanguage', 'chineseLanguage', 'mathematics', 'BIO')
 * @returns Weighting multiplier (defaults to 1.0 if not specified)
 */
export const getSubjectWeighting = (
  programme: ProgrammeEntry | undefined,
  subjectCode: string
): number => {
  if (!programme?.calculation?.specificSubjectWeighting) return 1.0;
  return programme.calculation.specificSubjectWeighting[subjectCode] || 1.0;
};

/**
 * Calculate elective scores with weightings
 * @param selectedElectives - Array of selected elective subjects
 * @param userScores - User's scores for all subjects
 * @param instKey - University key for Category C conversions
 * @param programme - Programme entry for subject weightings
 * @param scoreType - Which score to use ('min', 'current', 'max')
 * @returns Array of elective scores sorted by weighted score
 */
export const calculateElectiveScores = (
  selectedElectives: Array<{ subject: string; abbreviation: string }>,
  userScores: Record<string, ScoreTriple>,
  instKey: string,
  programme: ProgrammeEntry | undefined,
  scoreType: "min" | "current" | "max" = "current"
): ElectiveScore[] => {
  return selectedElectives
    .map((e) => {
      const grade = userScores[e.abbreviation]?.[scoreType] || "";
      let baseScore = 0;

      // Category C subjects use university-specific conversion
      if (["FRENCH", "GERMAN", "SPANISH", "KOREAN", "JAPANESE", "URDU"].includes(e.abbreviation)) {
        baseScore = getCategoryCLanguageScore(grade, instKey, e.abbreviation);
      } else {
        baseScore = gradeValues[grade] || 0;
      }

      // Apply subject-specific weighting
      // First try the full tokenized subject name (e.g., "informationAndCommunicationTechnology")
      // If that doesn't match, fall back to abbreviation (e.g., "ICT")
      const tokenizedSubject = tokenizeString(e.subject);
      const weighting = getSubjectWeighting(programme, tokenizedSubject);
      const weightedScore = baseScore * weighting;

      return {
        subject: e.subject,
        grade,
        score: weightedScore,
        baseScore,
        weighting: weighting !== 1.0 ? weighting : undefined,
      };
    })
    .sort((a, b) => b.score - a.score);
};

/**
 * Calculate total score for a programme
 * @param userScores - User's scores for all subjects
 * @param selectedElectives - Array of selected elective subjects
 * @param instKey - University key for Category C conversions
 * @param programme - Programme entry for subject weightings
 * @param scoreType - Which score to use ('min', 'current', 'max')
 * @returns Total weighted score
 */
export const calculateTotalScore = (
  userScores: Record<string, ScoreTriple>,
  selectedElectives: Array<{ subject: string; abbreviation: string }>,
  instKey: string,
  programme: ProgrammeEntry | undefined,
  scoreType: "min" | "current" | "max" = "current"
): number => {
  let total = 0;

  // Core subjects with weightings
  ["chineseLanguage", "englishLanguage", "mathematics"].forEach((subject) => {
    const grade = userScores[subject]?.[scoreType] || "";
    const baseScore = gradeValues[grade] || 0;
    const weighting = getSubjectWeighting(programme, subject);
    total += baseScore * weighting;
  });

  // Electives (best N based on programme requirements) with weightings
  const electiveScores = calculateElectiveScores(
    selectedElectives,
    userScores,
    instKey,
    programme,
    scoreType
  );

  // Determine how many electives to count based on BEST conditions
  const electiveConditions = programme?.calculation?.conditions?.filter((c: any) => 
    c.variable && c.variable.startsWith("BEST")
  ) || [];
  const numElectivesRequired = electiveConditions.length || 2; // Default to 2 if not specified

  total += electiveScores.slice(0, numElectivesRequired).reduce((sum, item) => sum + item.score, 0);

  return total;
};

/**
 * Get core subject breakdown with base scores and weightings
 * @param userScores - User's scores for all subjects
 * @param programme - Programme entry for subject weightings
 * @param scoreType - Which score to use ('min', 'current', 'max')
 * @returns Object with base scores and weightings for english, chinese, and mathematics
 */
export const getCoreSubjectBreakdown = (
  userScores: Record<string, ScoreTriple>,
  programme: ProgrammeEntry | undefined,
  scoreType: "min" | "current" | "max" = "max"
): {
  english: { baseScore: number; weighting: number; totalScore: number };
  chinese: { baseScore: number; weighting: number; totalScore: number };
  mathematics: { baseScore: number; weighting: number; totalScore: number };
} => {
  const engGrade = userScores["englishLanguage"]?.[scoreType] || "";
  const chinGrade = userScores["chineseLanguage"]?.[scoreType] || "";
  const mathGrade = userScores["mathematics"]?.[scoreType] || "";

  const engWeighting = getSubjectWeighting(programme, "englishLanguage");
  const chinWeighting = getSubjectWeighting(programme, "chineseLanguage");
  const mathWeighting = getSubjectWeighting(programme, "mathematics");

  const engBaseScore = gradeValues[engGrade] || 0;
  const chinBaseScore = gradeValues[chinGrade] || 0;
  const mathBaseScore = gradeValues[mathGrade] || 0;

  return {
    english: {
      baseScore: engBaseScore,
      weighting: engWeighting,
      totalScore: engBaseScore * engWeighting,
    },
    chinese: {
      baseScore: chinBaseScore,
      weighting: chinWeighting,
      totalScore: chinBaseScore * chinWeighting,
    },
    mathematics: {
      baseScore: mathBaseScore,
      weighting: mathWeighting,
      totalScore: mathBaseScore * mathWeighting,
    },
  };
};

/**
 * Calculate all scores (min, current, max) and breakdowns for a programme
 * NOTE: Currently uses MAX expected grades for calculation, then applies fixed offsets
 * @param userScores - User's scores for all subjects
 * @param selectedElectives - Array of selected elective subjects
 * @param instKey - University key for Category C conversions
 * @param programme - Programme entry for subject weightings
 * @returns Complete score calculation result with breakdowns
 */
export const calculateProgrammeScores = (
  userScores: Record<string, ScoreTriple>,
  selectedElectives: Array<{ subject: string; abbreviation: string }>,
  instKey: string,
  programme?: ProgrammeEntry
): ScoreCalculationResult => {
  // Calculate actual min, current, and max scores
  const minScore = calculateTotalScore(userScores, selectedElectives, instKey, programme, "min");
  const currentScore = calculateTotalScore(userScores, selectedElectives, instKey, programme, "current");
  const maxScore = calculateTotalScore(userScores, selectedElectives, instKey, programme, "max");

  // Calculate detailed breakdown using MAX scores
  const engGrade = userScores["englishLanguage"]?.max || "";
  const chinGrade = userScores["chineseLanguage"]?.max || "";
  const mathGrade = userScores["mathematics"]?.max || "";
  const csdGrade = userScores["citizenshipAndSocialDevelopment"]?.max || "";

  const engWeighting = getSubjectWeighting(programme, "englishLanguage");
  const chinWeighting = getSubjectWeighting(programme, "chineseLanguage");
  const mathWeighting = getSubjectWeighting(programme, "mathematics");

  const engScore = (gradeValues[engGrade] || 0) * engWeighting;
  const chinScore = (gradeValues[chinGrade] || 0) * chinWeighting;
  const mathScore = (gradeValues[mathGrade] || 0) * mathWeighting;
  

  // Calculate all electives ranked using MAX grades
  const allElectivesRanked = calculateElectiveScores(
    selectedElectives,
    userScores,
    instKey,
    programme,
    "max"
  );

  // Determine how many electives to count based on BEST conditions
  const electiveConditions = programme?.calculation?.conditions?.filter((c: any) => 
    c.variable && c.variable.startsWith("BEST")
  ) || [];
  const numElectivesRequired = electiveConditions.length || 2; // Default to 2 if not specified

  const bestElectives = allElectivesRanked.slice(0, numElectivesRequired);

  return {
    minScore,
    currentScore,
    maxScore,
    calculatedScores: {
      eng: engScore,
      chin: chinScore,
      math: mathScore,
      csd: csdGrade,
      bestElectives,
      allElectivesRanked,
    },
  };
};

